import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Button, Stack, Divider,
  List, ListItem, ListItemIcon, ListItemText, CircularProgress, Alert, Chip
} from '@mui/material';
import { Check, SportsScore, CreditCard, Lock, ArrowBack } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isNativeApp } from '../utils/platform';

const FEATURES = [
  'Profil coach public enrichi (diplômes, bio, liens)',
  'Sportifs illimités',
  'Planning intelligent (créneaux + séances directes)',
  'Création de programmes personnalisés',
  'Évaluation des performances sportifs',
  'Invitations sportifs par email',
  'Tableau de bord complet des séances',
  'Messagerie illimitée',
];

const SubscriptionCheckout = () => {
  const { tr } = useAppTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState<any>(null);
  const [loadingSub, setLoadingSub] = useState(true);

  // Dans l'app native (iOS/Android), aucune page d'abonnement/paiement n'est
  // accessible (regles des stores — pas de reference a un achat externe). On
  // renvoie immediatement vers le tableau de bord.
  useEffect(() => {
    if (isNativeApp()) navigate('/coach', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (isNativeApp()) { setLoadingSub(false); return; }
    const fetchSub = async () => {
      try {
        const res = await api.get('/payments/subscriptions/my_subscription/');
        setSubscription(res.data);
      } catch {
        setSubscription(null);
      } finally {
        setLoadingSub(false);
      }
    };
    fetchSub();
  }, []);

  if (isNativeApp()) return null;

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/payments/subscriptions/create_checkout_session/');
      window.open(res.data.checkout_url, '_blank');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la création de la session de paiement.');
      setLoading(false);
    }
  };

  const isActive = subscription?.status === 'ACTIVE';

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
      py: 6
    }}>
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ borderRadius: 4, overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ p: 4, background: 'linear-gradient(135deg, #6366F1 0%, #00BCD4 100%)', textAlign: 'center' }}>
            <SportsScore sx={{ fontSize: 56, color: '#fff', mb: 1 }} />
            <Typography variant="h4" fontWeight={900} color="white" gutterBottom>
               {tr("Abonnement Coach Pro")} </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h2" fontWeight={900} color="white">19.90€</Typography>
              <Typography variant="h6" color="rgba(255,255,255,0.8)">{tr("/mois")}</Typography>
            </Box>
            <Chip label={tr("Sans engagement · Annulable à tout moment")} sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
          </Box>

          {/* Body */}
          <Box sx={{ p: 4 }}>
            {loadingSub ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            ) : isActive ? (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                 {tr("Ton abonnement est déjà")} <strong>{tr("actif")}</strong>{tr(". Tu bénéficies de toutes les fonctionnalités Coach.")} </Alert>
            ) : (
              <>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{tr(error)}</Alert>}
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                   {tr("Ce qui est inclus :")} </Typography>
                <List dense disablePadding sx={{ mb: 3 }}>
                  {FEATURES.map((f, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.3 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Check sx={{ color: 'primary.main', fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography variant="body2">{f}</Typography>} />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ mb: 3 }} />
                <Stack spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CreditCard />}
                    onClick={handleCheckout}
                    disabled={loading}
                    sx={{ py: 1.8, borderRadius: 3, fontWeight: 800, fontSize: '1rem' }}
                  >
                    {loading ? 'Redirection vers Stripe...' : tr("Activer mon abonnement · 19.90€/mois")}
                  </Button>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Lock sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                       {tr("Paiement sécurisé via Stripe · Données cryptées SSL")} </Typography>
                  </Box>
                </Stack>
              </>
            )}
            <Button
              fullWidth variant="text" startIcon={<ArrowBack />}
              onClick={() => navigate('/coach/dashboard')}
              sx={{ mt: 2, borderRadius: 2 }}
            >
               {tr("Retour au dashboard")} </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SubscriptionCheckout;
